/**
 * Activity-first missions for Mystery Rover, Glow City, Breeze Station and Robot Artist.
 * Original block IDs and server-persisted fields are not modified.
 * Each phase's authored learner blocks are assigned exactly once to one purposeful activity.
 * A focused activity is an investigation, not a single-question slideshow.
 */
export const OTHER_WONDERLABS_FLOW = {
  "wind-car-prelude": [
    {
      "id": "green-valley-story-prelude",
      "title": "Enter the Green Valley rescue",
      "purpose": "Watch the rescue story and examine Mission Control’s constraints.",
      "blockIds": [
        "green-valley-story-prelude",
        "rescue-alert"
      ]
    },
    {
      "id": "rescue-image",
      "title": "Read the rescue message",
      "purpose": "Inspect the ranger’s briefing before proposing a solution.",
      "blockIds": [
        "rescue-image"
      ]
    }
  ],
  "wind-car-wonder": [
    {
      "id": "solution-prediction",
      "title": "Make your first guess",
      "purpose": "Predict what might move the rover, then record your own opening theory.",
      "blockIds": [
        "solution-prediction",
        "initial-theory"
      ]
    }
  ],
  "wind-car-explore": [
    {
      "id": "step2-story",
      "title": "Look for clues on the rover",
      "purpose": "Inspect the model, parts, and visible connections before drawing a conclusion.",
      "blockIds": [
        "step2-story",
        "rover-image-gallery",
        "mainmenu-pwo-block"
      ]
    },
    {
      "id": "rover-observations",
      "title": "Record what you can see",
      "purpose": "Choose only clues actually supported by what you inspected.",
      "blockIds": [
        "rover-observations"
      ]
    },
    {
      "id": "motion-source",
      "title": "Investigate the source of motion",
      "purpose": "Use evidence and the science video to reconsider what pushes the rover.",
      "blockIds": [
        "motion-source",
        "science-rover-video-block",
        "motor-job-check"
      ]
    },
    {
      "id": "step2-explanation",
      "title": "Revise your first theory",
      "purpose": "Explain how your thinking changed after inspecting the rover.",
      "blockIds": [
        "step2-explanation"
      ]
    }
  ],
  "wind-car-understand": [
    {
      "id": "step3-story",
      "title": "Explore the virtual parts tray",
      "purpose": "Open the 3D parts tray again. This time, identify the components and compare them with your real kit.",
      "blockIds": [
        "step3-story",
        "accessories-pwo-block",
        "parts-gallery",
        "parts-found"
      ]
    },
    {
      "id": "component-jobs",
      "title": "Match parts to their jobs",
      "purpose": "Use the model and real components to match each part to its job.",
      "blockIds": ["component-jobs"]
    },
    {
      "id": "system-video-block",
      "title": "See the complete system",
      "purpose": "Watch how the parts work together, then use that evidence in your next investigation.",
      "blockIds": ["system-video-block"]
    },
    {
      "id": "power-path",
      "title": "Trace power through the rover",
      "purpose": "Follow the complete energy-and-motion path and test your circuit reasoning.",
      "blockIds": [
        "power-path",
        "closed-circuit-check",
        "power-explanation"
      ]
    },
    {
      "id": "step4-story",
      "title": "Predict the direction of the air",
      "purpose": "Examine the diagram and predict where the propeller sends air.",
      "blockIds": [
        "step4-story",
        "air-diagram",
        "air-prediction"
      ]
    },
    {
      "id": "backward-push-video-block",
      "title": "Observe the backward push",
      "purpose": "Watch the relevant science, then record what the air actually does.",
      "blockIds": [
        "backward-push-video-block",
        "balloon-pwo-block",
        "demo-observation"
      ]
    },
    {
      "id": "thrust-check",
      "title": "Explain why the rover moves",
      "purpose": "Apply action and reaction to explain the direction of motion.",
      "blockIds": [
        "thrust-check",
        "reverse-wire-check",
        "backward-explanation"
      ]
    }
  ],
  "wind-car-apply": [
    {
      "id": "step5-story",
      "title": "Verify your kit before building",
      "purpose": "You identified the parts earlier. Now check the real kit is still complete, all hardware is safe, and power is disconnected before assembly.",
      "blockIds": [
        "step5-story",
        "readiness-gallery",
        "parts-readiness",
        "safety-checks",
        "failure-prediction"
      ]
    },
    {
      "id": "build-plan-note",
      "title": "Make your prevention plan",
      "purpose": "Write a realistic plan before opening the powered system.",
      "blockIds": [
        "build-plan-note"
      ]
    },
    {
      "id": "step6-story",
      "title": "Assemble the rescue rover",
      "purpose": "Follow the 3D installation guide with power disconnected.",
      "blockIds": [
        "step6-story",
        "installation-pwo-block",
        "installation-gallery"
      ]
    },
    {
      "id": "assembly-checklist",
      "title": "Check the assembled rover",
      "purpose": "Verify mechanical movement and power-off conditions before testing.",
      "blockIds": [
        "assembly-checklist",
        "wheel-check",
        "power-off-confirm"
      ]
    },
    {
      "id": "assembly-note",
      "title": "Record a build observation",
      "purpose": "Describe one successful check or real assembly challenge.",
      "blockIds": [
        "assembly-note"
      ]
    }
  ],
  "wind-car-test-improve": [
    {
      "id": "step7-story",
      "title": "Diagnose your first power-on",
      "purpose": "Follow the safe startup sequence and record the first powered result.",
      "blockIds": [
        "step7-story",
        "diagnostic-image",
        "build-summary",
        "first-spin-record"
      ]
    },
    {
      "id": "open-circuit-diagnostic",
      "title": "Choose a diagnostic check if needed",
      "purpose": "If your motor ran correctly, explain what you would check first if it stopped. If it failed, diagnose the actual problem safely.",
      "blockIds": [
        "open-circuit-diagnostic",
        "rover-master-teacher-diagnostic",
        "diagnostic-note"
      ]
    },
    {
      "id": "step8-story",
      "title": "Prepare the rescue lane",
      "purpose": "Set up a fair lane and predict the rover’s first run.",
      "blockIds": [
        "step8-story",
        "lane-image",
        "trial-prior-work",
        "trial1-prediction"
      ]
    },
    {
      "id": "trial1-record",
      "title": "Run and record Trial 1",
      "purpose": "Test the rover, observe its travel, and record its actual performance.",
      "blockIds": [
        "trial1-record",
        "friction-video-block",
        "friction-check",
        "trial1-observation"
      ]
    },
    {
      "id": "first-run-problem",
      "title": "Decide what to improve",
      "purpose": "Identify a real limitation supported by your first test.",
      "blockIds": [
        "first-run-problem"
      ]
    },
    {
      "id": "step9-story",
      "title": "Plan one redesign variable",
      "purpose": "Review Trial 1, choose one change, and make a prediction.",
      "blockIds": [
        "step9-story",
        "fair-test-image",
        "trial1-summary",
        "redesign-variable",
        "redesign-prediction",
        "taming-thrust-video-block"
      ]
    },
    {
      "id": "trial2-record",
      "title": "Test your redesign",
      "purpose": "Repeat the trial under comparable conditions and record Trial 2.",
      "blockIds": [
        "trial2-record"
      ]
    },
    {
      "id": "comparison-claim",
      "title": "Compare Trial 1 and Trial 2",
      "purpose": "Use recorded evidence to judge whether the redesign helped.",
      "blockIds": [
        "comparison-claim",
        "redesign-conclusion"
      ]
    }
  ],
  "wind-car-explain": [
    {
      "id": "step10-story",
      "title": "Assemble your rescue evidence",
      "purpose": "Review the system and your test records before presenting a claim.",
      "blockIds": [
        "step10-story",
        "final-chain-image",
        "final-prior-work"
      ]
    },
    {
      "id": "final-energy-chain",
      "title": "Explain the complete chain",
      "purpose": "Connect battery, circuit, motor, propeller, air, and rover movement.",
      "blockIds": [
        "final-energy-chain"
      ]
    },
    {
      "id": "engineer-report",
      "title": "Present your rescue report",
      "purpose": "Use your actual trial results to explain what worked and what did not.",
      "blockIds": [
        "engineer-report"
      ]
    }
  ],
  "wind-car-reflect": [
    {
      "id": "reflect-story",
      "title": "Revisit your first theory",
      "purpose": "Use previous observations to think about how your understanding developed.",
      "blockIds": [
        "reflect-story",
        "reflect-prior-work"
      ]
    },
    {
      "id": "final-reflection",
      "title": "What changed your thinking?",
      "purpose": "Explain what evidence surprised you and what you would do differently.",
      "blockIds": [
        "final-reflection"
      ]
    }
  ],
  "wind-car-grow": [
    {
      "id": "grow-story",
      "title": "Connect the rover to real engineering",
      "purpose": "Compare your air-push rover with other wind-related technologies.",
      "blockIds": [
        "grow-story",
        "real-wind-video-block",
        "honesty-check"
      ],
      "optionalBlockIds": [
        "after-camp-green-valley-mission",
        "after-camp-green-valley-choice",
        "after-camp-green-valley-plan",
        "after-camp-green-valley-safety",
        "after-camp-green-valley-trials",
        "after-camp-green-valley-conclusion"
      ],
      "optionalTitle": "Explore an independent challenge (optional)"
    }
  ],
  "conductive-dough-prelude": [
    {
      "id": "glow-city-story-prelude",
      "title": "Enter Glow City",
      "purpose": "Watch the blackout story and learn the mission constraints.",
      "blockIds": [
        "glow-city-story-prelude",
        "blackout-alert"
      ]
    },
    {
      "id": "blackout-reveal",
      "title": "Inspect the emergency clues",
      "purpose": "Read the sculptor’s message and examine the last working landmark.",
      "blockIds": [
        "blackout-reveal",
        "evidence-gallery"
      ]
    }
  ],
  "conductive-dough-wonder": [
    {
      "id": "opening-cause",
      "title": "Make an opening hypothesis",
      "purpose": "Predict what caused the blackout and begin your evidence notebook.",
      "blockIds": [
        "opening-cause",
        "opening-observations"
      ]
    }
  ],
  "conductive-dough-explore": [
    {
      "id": "step2-story",
      "title": "Predict which materials carry current",
      "purpose": "Use the virtual lab and compare materials before deciding.",
      "blockIds": [
        "step2-story",
        "material-prediction",
        "mainmenu-pwo",
        "materials-pwo",
        "material-gallery"
      ]
    },
    {
      "id": "material-jobs",
      "title": "Identify the materials’ jobs",
      "purpose": "Match conductive and insulating materials to their roles.",
      "blockIds": [
        "material-jobs"
      ]
    },
    {
      "id": "road-explanation",
      "title": "Explain the first circuit clue",
      "purpose": "Use your evidence to explain how soft material can carry current.",
      "blockIds": [
        "road-explanation"
      ]
    },
    {
      "id": "step3-story",
      "title": "Predict the two-path circuit",
      "purpose": "Examine why current needs a route out and back.",
      "blockIds": [
        "step3-story",
        "two-road-prediction"
      ]
    },
    {
      "id": "step1-pwo",
      "title": "Build and trace the virtual loop",
      "purpose": "Use the 3D guide and circuit map before explaining the separation.",
      "blockIds": [
        "step1-pwo",
        "step2-pwo",
        "two-road-gallery",
        "loop-sequence"
      ]
    },
    {
      "id": "gap-reason",
      "title": "Explain the gap",
      "purpose": "Use the complete-loop evidence to defend why the paths stay apart.",
      "blockIds": [
        "gap-reason"
      ]
    }
  ],
  "conductive-dough-understand": [
    {
      "id": "step4-story",
      "title": "Predict the LED direction",
      "purpose": "Form a prediction before swapping the LED terminals.",
      "blockIds": [
        "step4-story",
        "polarity-prediction",
        "polarity-gallery"
      ]
    },
    {
      "id": "step3-pwo",
      "title": "Investigate the one-way light gate",
      "purpose": "Inspect the virtual LED and safely compare both directions.",
      "blockIds": [
        "step3-pwo",
        "step4-pwo",
        "dark-led-video"
      ]
    },
    {
      "id": "polarity-record",
      "title": "Record what happened",
      "purpose": "Use the polarity evidence to diagnose why the LED was dark.",
      "blockIds": [
        "polarity-record",
        "polarity-check"
      ]
    }
  ],
  "conductive-dough-apply": [
    {
      "id": "step5-story",
      "title": "Prepare for the first light",
      "purpose": "Inspect the kit and check the power-off rules before building.",
      "blockIds": [
        "step5-story",
        "kit-gallery",
        "assembly-pwo",
        "kit-inventory",
        "physical-safety"
      ]
    },
    {
      "id": "first-light-build",
      "title": "Build the first Glow City light",
      "purpose": "Follow the physical and virtual guides with an adult when needed.",
      "blockIds": [
        "first-light-build"
      ]
    },
    {
      "id": "first-light-record",
      "title": "Record the first light",
      "purpose": "Capture the real outcome and diagnose only from observed evidence.",
      "blockIds": [
        "first-light-record"
      ]
    }
  ],
  "conductive-dough-test-improve": [
    {
      "id": "step6-story",
      "title": "Predict the shortcut fault",
      "purpose": "Examine the accidental competing path before changing anything.",
      "blockIds": [
        "step6-story",
        "shortcut-prediction",
        "shortcut-gallery",
        "short-video"
      ]
    },
    {
      "id": "fault-classification",
      "title": "Diagnose the short circuit",
      "purpose": "Classify clues and explain the safest repair approach.",
      "blockIds": [
        "fault-classification",
        "shortcut-sequence",
        "safe-short-fix"
      ]
    },
    {
      "id": "step7-story",
      "title": "Plan an insulating wall",
      "purpose": "Choose where insulation belongs and explore how it separates paths.",
      "blockIds": [
        "step7-story",
        "wall-placement",
        "wall-gallery",
        "exploremore-pwo"
      ]
    },
    {
      "id": "wall-jobs",
      "title": "Build a protective wall",
      "purpose": "Explain its role, safely make it, and compare before and after.",
      "blockIds": [
        "wall-jobs",
        "wall-build-check",
        "wall-comparison"
      ]
    },
    {
      "id": "step8-story",
      "title": "Design your Glow City landmark",
      "purpose": "Choose a meaningful design using the electrical constraints.",
      "blockIds": [
        "step8-story",
        "landmark-gallery",
        "landmark-type",
        "criteria-constraints"
      ]
    },
    {
      "id": "design-plan",
      "title": "Build and test the landmark",
      "purpose": "Write a blueprint, construct it, and capture Trial 1.",
      "blockIds": [
        "design-plan",
        "landmark-build",
        "landmark-first-trial"
      ]
    },
    {
      "id": "step9-story",
      "title": "Investigate the first failure",
      "purpose": "Review Trial 1 and identify the strongest evidence of a fault.",
      "blockIds": [
        "step9-story",
        "troubleshooting-image",
        "trial1-summary",
        "fault-hypothesis"
      ]
    },
    {
      "id": "redesign-variable",
      "title": "Change just one thing",
      "purpose": "Select one improvement, predict its effect, and test again.",
      "blockIds": [
        "redesign-variable",
        "redesign-prediction"
      ]
    },
    {
      "id": "trial2-record",
      "title": "Compare your second landmark test",
      "purpose": "Record Trial 2 and explain whether the single change helped.",
      "blockIds": [
        "trial2-record",
        "redesign-conclusion"
      ]
    }
  ],
  "conductive-dough-explain": [
    {
      "id": "step10-story",
      "title": "Gather Glow City evidence",
      "purpose": "Revisit the circuit and the landmark trials.",
      "blockIds": [
        "step10-story",
        "final-gallery",
        "prior-evidence"
      ]
    },
    {
      "id": "final-chain",
      "title": "Defend the working circuit",
      "purpose": "Build the causal chain, check scientific honesty, and report your evidence.",
      "blockIds": [
        "final-chain",
        "honesty-check",
        "rescue-report"
      ]
    }
  ],
  "conductive-dough-reflect": [
    {
      "id": "conductive-reflect-story",
      "title": "Look back at the blackout",
      "purpose": "Recall how your explanation changed as you built and tested.",
      "blockIds": [
        "conductive-reflect-story",
        "final-reflection"
      ]
    }
  ],
  "conductive-dough-grow": [
    {
      "id": "conductive-grow-story",
      "title": "Invent the next Glow City idea",
      "purpose": "Transfer the circuit rules to a fresh design challenge.",
      "blockIds": [
        "conductive-grow-story",
        "conductive-grow-transfer"
      ],
      "optionalBlockIds": [
        "after-camp-glow-city-mission",
        "after-camp-mission-choice",
        "after-camp-plan",
        "after-camp-safety-check",
        "after-camp-three-trials",
        "after-camp-evidence-conclusion"
      ],
      "optionalTitle": "Explore an independent challenge (optional)"
    }
  ],
  "electric-spiral-prelude": [
    {
      "id": "breeze-station-story-prelude",
      "title": "Enter the silent station",
      "purpose": "Watch the story and distinguish the two machine failures.",
      "blockIds": [
        "breeze-station-story-prelude",
        "station-alert"
      ]
    },
    {
      "id": "station-reveal",
      "title": "Read the engineer’s message",
      "purpose": "Inspect both unopened kits and identify real clues.",
      "blockIds": [
        "station-reveal",
        "two-kits-gallery"
      ]
    }
  ],
  "electric-spiral-wonder": [
    {
      "id": "opening-cause",
      "title": "Make your first diagnosis",
      "purpose": "Predict a cause and record what evidence could test it.",
      "blockIds": [
        "opening-cause",
        "opening-record"
      ]
    }
  ],
  "electric-spiral-explore": [
    {
      "id": "path-story",
      "title": "Predict what a gap will do",
      "purpose": "Think through a complete low-voltage power loop.",
      "blockIds": [
        "path-story",
        "gap-prediction",
        "path-gallery"
      ]
    },
    {
      "id": "loop-sequence",
      "title": "Trace the complete circuit",
      "purpose": "Arrange the parts and explain why electrical power needs a return path.",
      "blockIds": [
        "loop-sequence",
        "switch-check",
        "loop-explanation"
      ]
    },
    {
      "id": "parta-story",
      "title": "Prepare the signal beacon",
      "purpose": "Inspect the exact Part A kit and check power-off readiness.",
      "blockIds": [
        "parta-story",
        "parta-gallery",
        "parta-readiness"
      ]
    },
    {
      "id": "parta-build",
      "title": "Build and test the beacon",
      "purpose": "Construct the first system, record its light, and diagnose its contacts.",
      "blockIds": [
        "parta-build",
        "parta-trial",
        "parta-check"
      ]
    }
  ],
  "electric-spiral-understand": [
    {
      "id": "bridge-story",
      "title": "Compare light and motion",
      "purpose": "Find the common electrical rules and different outputs.",
      "blockIds": [
        "bridge-story",
        "same-different",
        "comparison-gallery"
      ]
    },
    {
      "id": "parta-chain",
      "title": "Predict the Wind Engine chain",
      "purpose": "Use the beacon evidence to build both energy chains.",
      "blockIds": [
        "parta-chain",
        "partb-predicted-chain",
        "power-loop-video"
      ]
    },
    {
      "id": "bridge-explanation",
      "title": "Explain what transfers",
      "purpose": "State what the signal beacon teaches about the wind machine.",
      "blockIds": [
        "bridge-explanation"
      ]
    },
    {
      "id": "partb-reveal-story",
      "title": "Explore the Wind Engine parts",
      "purpose": "Inspect the virtual kit and assign each component its job.",
      "blockIds": [
        "partb-reveal-story",
        "partb-mainmenu",
        "partb-materials",
        "partb-gallery",
        "component-jobs"
      ]
    },
    {
      "id": "component-explanation",
      "title": "Predict a weak point",
      "purpose": "Choose a likely failure before handling the machine.",
      "blockIds": [
        "component-explanation"
      ]
    }
  ],
  "electric-spiral-apply": [
    {
      "id": "tower-story",
      "title": "Prepare to build the Breeze Tower",
      "purpose": "Inspect the installation guide and physical components.",
      "blockIds": [
        "tower-story",
        "assembly-gallery",
        "assembly-pwo"
      ]
    },
    {
      "id": "partb-safety",
      "title": "Assemble with power disconnected",
      "purpose": "Verify physical safety and follow the step-by-step build.",
      "blockIds": [
        "partb-safety",
        "partb-build"
      ]
    },
    {
      "id": "assembly-risk",
      "title": "Predict an assembly risk",
      "purpose": "Use actual kit evidence to identify a weak point.",
      "blockIds": [
        "assembly-risk",
        "assembly-note"
      ]
    }
  ],
  "electric-spiral-test-improve": [
    {
      "id": "wake-story",
      "title": "Plan the first wind test",
      "purpose": "Predict what should happen, and confirm safe power-on conditions.",
      "blockIds": [
        "wake-story",
        "test-gallery",
        "first-run-prediction",
        "first-run-checklist"
      ]
    },
    {
      "id": "first-run-record",
      "title": "Observe the first run",
      "purpose": "Record motion, airflow, and the observed power-to-motion chain.",
      "blockIds": [
        "first-run-record",
        "wind-chain",
        "learned-pwo"
      ]
    },
    {
      "id": "reverse-story",
      "title": "Predict a direction change",
      "purpose": "Plan a fair test before reversing the motor leads.",
      "blockIds": [
        "reverse-story",
        "reverse-gallery",
        "reverse-prediction",
        "fair-test-plan"
      ]
    },
    {
      "id": "direction-safety",
      "title": "Compare directions safely",
      "purpose": "Power off before adjustments and record the two results.",
      "blockIds": [
        "direction-safety",
        "direction-record"
      ]
    },
    {
      "id": "reverse-check",
      "title": "Explain the changed airflow",
      "purpose": "Use your evidence to reason about direction, not just rotation.",
      "blockIds": [
        "reverse-check",
        "direction-video"
      ]
    },
    {
      "id": "rescue-story",
      "title": "Investigate a silent or shaky motor",
      "purpose": "Use the safe troubleshooting sequence to classify possible causes.",
      "blockIds": [
        "rescue-story",
        "troubleshoot-gallery",
        "fault-classification",
        "diagnostic-sequence",
        "trial1-summary"
      ]
    },
    {
      "id": "fault-hypothesis",
      "title": "Choose one diagnosis",
      "purpose": "Compare the recorded evidence with your current hypothesis.",
      "blockIds": [
        "fault-hypothesis"
      ]
    },
    {
      "id": "redesign-record",
      "title": "Test a safe fix",
      "purpose": "Change one thing, record both trials, and reassess the explanation.",
      "blockIds": [
        "redesign-record",
        "diagnostics-video",
        "safety-quiz"
      ]
    }
  ],
  "electric-spiral-explain": [
    {
      "id": "final-story",
      "title": "Gather your station evidence",
      "purpose": "Revisit the two machines and your observed experiments.",
      "blockIds": [
        "final-story",
        "final-gallery",
        "prior-evidence"
      ]
    },
    {
      "id": "final-chain",
      "title": "Explain how the Wind Engine works",
      "purpose": "Trace the causal chain and prepare an evidence-based engineer report.",
      "blockIds": [
        "final-chain",
        "final-honesty",
        "engineer-report"
      ]
    }
  ],
  "electric-spiral-reflect": [
    {
      "id": "electric-reflect-story",
      "title": "How did your theory change?",
      "purpose": "Compare your earliest ideas with the observed outcomes.",
      "blockIds": [
        "electric-reflect-story",
        "final-reflection"
      ]
    }
  ],
  "electric-spiral-grow": [
    {
      "id": "electric-grow-story",
      "title": "Imagine another powered machine",
      "purpose": "Apply the same circuit principle to a different useful output.",
      "blockIds": [
        "electric-grow-story",
        "electric-grow-transfer"
      ]
    }
  ],
  "robot-artist-prelude": [
    {
      "id": "robot-artist-story-prelude",
      "title": "Enter the Robot Artist mystery",
      "purpose": "Watch the story and learn the investigator’s rules.",
      "blockIds": [
        "robot-artist-story-prelude",
        "robot-prelude-rule-check"
      ]
    }
  ],
  "robot-artist-wonder": [
    {
      "id": "case-update",
      "title": "Discover the surprise artist",
      "purpose": "Uncover the artist and revisit your first ideas.",
      "blockIds": [
        "case-update",
        "robot-reveal"
      ]
    },
    {
      "id": "theory-comparison",
      "title": "Compare the new evidence",
      "purpose": "Revise your hypothesis using what was actually revealed.",
      "blockIds": [
        "theory-comparison",
        "clue-now-makes-sense"
      ]
    },
    {
      "id": "robot-abilities",
      "title": "Define the engineering mission",
      "purpose": "Identify what the robot must do and which constraints matter.",
      "blockIds": [
        "robot-abilities",
        "criteria-constraints",
        "mission-goal"
      ]
    },
    {
      "id": "engineering-video",
      "title": "Meet the robot and its movement",
      "purpose": "Explore video and 3D evidence before moving into the parts investigation.",
      "blockIds": [
        "engineering-video",
        "meet-robot-pwo"
      ]
    }
  ],
  "robot-artist-explore": [
    {
      "id": "step3-story",
      "title": "Inspect the robot components",
      "purpose": "Compare the virtual parts with the real kit.",
      "blockIds": [
        "step3-story",
        "accessories-pwo",
        "kit-evidence-gallery",
        "real-kit-check"
      ]
    },
    {
      "id": "part-systems",
      "title": "Identify each system’s job",
      "purpose": "Group and match components without guessing their functions.",
      "blockIds": [
        "part-systems",
        "part-jobs"
      ]
    },
    {
      "id": "mystery-weight-hypothesis",
      "title": "Investigate the mystery weight",
      "purpose": "Use the available evidence to form and defend a hypothesis.",
      "blockIds": [
        "mystery-weight-hypothesis",
        "mystery-weight-reason"
      ]
    },
    {
      "id": "student-kit-question",
      "title": "Ask your own engineering question",
      "purpose": "Write the question your investigation will try to answer.",
      "blockIds": [
        "student-kit-question"
      ]
    }
  ],
  "robot-artist-understand": [
    {
      "id": "step4-story",
      "title": "Predict where motion starts",
      "purpose": "Find the energy source and inspect the early motion evidence.",
      "blockIds": [
        "step4-story",
        "energy-start-prediction",
        "energy-evidence-gallery",
        "energy-intro-video",
        "energy-forms-pwo",
        "battery-pwo"
      ]
    },
    {
      "id": "energy-pathway",
      "title": "Trace the energy pathway",
      "purpose": "Connect the battery, motor, and movement with evidence.",
      "blockIds": [
        "energy-pathway",
        "system-energy-jobs",
        "motor-energy-check"
      ]
    },
    {
      "id": "motion-plan",
      "title": "Predict the first artwork",
      "purpose": "Use energy and motion to plan what the robot might draw.",
      "blockIds": [
        "motion-plan",
        "art-pattern-plan",
        "power-plan-evidence"
      ]
    },
    {
      "id": "step5-story",
      "title": "Diagnose a silent motor",
      "purpose": "Inspect circuit clues and predict the first safe check.",
      "blockIds": [
        "step5-story",
        "circuit-images",
        "circuit-first-prediction"
      ]
    },
    {
      "id": "circuit-video",
      "title": "Trace the electrical road",
      "purpose": "Use the video and virtual 3D guide to complete the circuit loop.",
      "blockIds": [
        "circuit-video",
        "closed-switch-pwo",
        "circuit-path-sequence",
        "circuit-jobs",
        "open-wire-check"
      ]
    },
    {
      "id": "physical-circuit-checklist",
      "title": "Test the actual circuit",
      "purpose": "Verify the powered system safely and record your diagnosis.",
      "blockIds": [
        "physical-circuit-checklist",
        "circuit-evidence"
      ]
    },
    {
      "id": "step6-story",
      "title": "Predict the wobble mystery",
      "purpose": "Inspect centered and off-center movement before watching evidence.",
      "blockIds": [
        "step6-story",
        "wobble-images",
        "balanced-offcenter-prediction"
      ]
    },
    {
      "id": "dancing-robot-video",
      "title": "Investigate vibration",
      "purpose": "Explore inertia and the motor to build a wobble model.",
      "blockIds": [
        "dancing-robot-video",
        "motor-animation-pwo",
        "inertia-pwo"
      ]
    },
    {
      "id": "wobble-sequence",
      "title": "Explain the drawing motion",
      "purpose": "Use your observations to explain the wobbly artwork.",
      "blockIds": [
        "wobble-sequence",
        "motion-causes",
        "center-weight-check",
        "wobble-evidence"
      ]
    }
  ],
  "robot-artist-apply": [
    {
      "id": "step7-story",
      "title": "Prepare to build the artist",
      "purpose": "Open the assembly guide and plan a safe launch.",
      "blockIds": [
        "step7-story",
        "assembly-gallery",
        "assembly-video",
        "installation-pwo",
        "doodle-robot-threejs-pilot"
      ]
    },
    {
      "id": "assembly-phase-checklist",
      "title": "Assemble and predict",
      "purpose": "Build the prototype and predict its first movement.",
      "blockIds": [
        "assembly-phase-checklist",
        "launch-prediction"
      ]
    },
    {
      "id": "first-test-record",
      "title": "Run the first art test",
      "purpose": "Record the real artwork and what its pattern reveals.",
      "blockIds": [
        "first-test-record",
        "drawing-as-data",
        "mapping-wobble-video"
      ]
    }
  ],
  "robot-artist-test-improve": [
    {
      "id": "step8-story",
      "title": "Review Test 1 and plan a change",
      "purpose": "Choose a single variable and predict how it affects the art.",
      "blockIds": [
        "step8-story",
        "test1-case-file",
        "redesign-gallery",
        "redesign-variable",
        "redesign-prediction"
      ]
    },
    {
      "id": "redesign-video",
      "title": "Investigate the redesign idea",
      "purpose": "Use media evidence to decide what to look for in Test 2.",
      "blockIds": [
        "redesign-video",
        "wobble-power-video",
        "friction-station"
      ]
    },
    {
      "id": "test2-record",
      "title": "Run Test 2",
      "purpose": "Record the new artwork and compare it to your original goal.",
      "blockIds": [
        "test2-record",
        "improvement-conclusion",
        "test-comparison"
      ]
    }
  ],
  "robot-artist-explain": [
    {
      "id": "step9-story",
      "title": "Gather your evidence",
      "purpose": "Review both artworks and the energy-to-artwork mechanism.",
      "blockIds": [
        "step9-story",
        "tests-summary",
        "system-gallery"
      ]
    },
    {
      "id": "energy-motion-sequence",
      "title": "Build the full system model",
      "purpose": "Explain how circuit and vibration produce the motion.",
      "blockIds": [
        "energy-motion-sequence",
        "system-jobs",
        "imbalance-recap-video",
        "circuit-recap-video"
      ]
    },
    {
      "id": "circuit-defense",
      "title": "Defend your mechanism",
      "purpose": "Check both the circuit and the vibration pathway.",
      "blockIds": [
        "circuit-defense",
        "motion-defense"
      ]
    },
    {
      "id": "final-science-explanation",
      "title": "Write the engineer’s explanation",
      "purpose": "Connect the model with actual drawing evidence.",
      "blockIds": [
        "final-science-explanation",
        "evidence-for-explanation"
      ]
    }
  ],
  "robot-artist-reflect": [
    {
      "id": "robot-reflect-story",
      "title": "How did your thinking change?",
      "purpose": "Revisit the initial mystery and what the tests taught you.",
      "blockIds": [
        "robot-reflect-story",
        "final-case-summary",
        "final-reflection"
      ]
    }
  ],
  "robot-artist-grow": [
    {
      "id": "step10-story",
      "title": "Prepare the Robot Art Gallery",
      "purpose": "Collect the art and give your creation a name.",
      "blockIds": [
        "step10-story",
        "gallery-images",
        "robot-name",
        "artwork-title"
      ]
    },
    {
      "id": "gallery-piece",
      "title": "Choose your gallery evidence",
      "purpose": "Decide which artwork best supports your story.",
      "blockIds": [
        "gallery-piece"
      ]
    },
    {
      "id": "gallery-presentation",
      "title": "Present the artist-engineer report",
      "purpose": "Prepare the gallery presentation and defend your reasoning.",
      "blockIds": [
        "gallery-presentation",
        "engineers-defense",
        "completion-pwo"
      ],
      "optionalBlockIds": [
        "after-camp-robot-artist-mission",
        "after-camp-robot-artist-choice",
        "after-camp-robot-artist-plan",
        "after-camp-robot-artist-safety",
        "after-camp-robot-artist-trials",
        "after-camp-robot-artist-conclusion"
      ],
      "optionalTitle": "Explore an independent challenge (optional)"
    }
  ]
};
